import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import { GeocodeResult } from "@mapmycustomers/shared/types/base/Located";
import LongLat from "@mapmycustomers/shared/types/base/LongLat";
import { Company, EntityType, Person } from "@mapmycustomers/shared/types/entity";
import { AreaFilterCondition } from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";
import {
  convertCoordinatesToLongLat,
  convertLatLngToLongLat,
  convertLongLatToLatLngLiteral,
} from "@mapmycustomers/shared/util/geo/GeoService";
import { OptionalFields } from "@mapmycustomers/shared/util/ts";
import { Button } from "@mapmycustomers/ui";

import AreaSearchInput from "@app/component/input/AreaSearchInput";
import { isEntityAreaSearchQuery } from "@app/component/input/AreaSearchInput/utils/assert";
import localSettings from "@app/config/LocalSettings";
import AreaSearchMode from "@app/enum/AreaSearchMode";
import { fetchCompany } from "@app/store/company/actions";
import { reverseGeocodeAddress } from "@app/store/location/actions";
import { fetchPerson } from "@app/store/person/actions";
import { AreaSearchQuery } from "@app/types/filters/AreaSearchQuery";
import { METERS_IN_MILE } from "@app/util/consts";
import { isEntityValue } from "@app/util/filters/Location/assert";
import {
  CUSTOM_DISTANCES_LIMIT,
  DEFAULT_DISTANCE_KM,
  DEFAULT_DISTANCE_MILES,
  DISTANCES_KM,
  DISTANCES_MILES,
} from "@app/util/filters/Location/const";
import roundToPrecision from "@app/util/number/roundToPrecision";

import AreaFilterMap from "./AreaFilterMap";
import styles from "./LocationFilterPopover.module.scss";

interface LocationFilterPopoverProps {
  mapCenter?: LongLat;
  onChange?: (value: AreaFilterCondition) => void;
  onFetchCompany: typeof fetchCompany;
  onFetchPerson: typeof fetchPerson;
  onHide?: () => void;
  onReverseGeocodeAddress: typeof reverseGeocodeAddress;
  userPosition?: GeolocationPosition;
  value?: OptionalFields<AreaFilterCondition, "value">; // e.g. when you just added location as first filter
}

const LocationFilterPopover = ({
  mapCenter,
  onChange,
  onFetchCompany,
  onFetchPerson,
  onHide,
  onReverseGeocodeAddress,
  userPosition,
  value,
}: LocationFilterPopoverProps) => {
  const intl = useIntl();

  const usesSiUnits = localSettings.doesUseSiUnits();

  const [query, setQuery] = useState<AreaSearchQuery | undefined>();
  useEffect(
    () => {
      if (isEntityValue(value?.value)) {
        if (value!.value.entity.type === EntityType.COMPANY) {
          onFetchCompany({
            id: value!.value.entity.id,
            callback: (company: Company) =>
              setQuery({ entity: company, mode: AreaSearchMode.ENTITY }),
          });
        } else if (value!.value.entity.type === EntityType.PERSON) {
          onFetchPerson({
            id: value!.value.entity.id,
            callback: (person: Person) => setQuery({ entity: person, mode: AreaSearchMode.ENTITY }),
          });
        }
      } else if (value?.value) {
        setQuery({
          address: value.value.location ?? "",
          coordinates: [value.value.longitude, value.value.latitude] as LongLat,
          mode: AreaSearchMode.ADDRESS,
        });
      } else {
        if (userPosition) {
          const coordinates = convertCoordinatesToLongLat(userPosition.coords);
          onReverseGeocodeAddress({
            callback: (result: GeocodeResult) => {
              // do not re-assign query if it is set already
              setQuery(
                (query) =>
                  query ?? {
                    address: result.address.formattedAddress ?? "",
                    coordinates,
                    mode: AreaSearchMode.CURRENT_USER_LOCATION,
                  }
              );
            },
            coordinates,
            failureCallback: () => {
              // do not re-assign query if it is set already
              setQuery(
                (query) =>
                  query ?? {
                    address: coordinates.join(", "),
                    coordinates,
                    mode: AreaSearchMode.CURRENT_USER_LOCATION,
                  }
              );
            },
          });
        } else {
          // do not re-assign query if it is set already
          setQuery(
            (query) =>
              query ?? {
                address: "",
                mode: AreaSearchMode.ADDRESS,
              }
          );
        }
      }
    },
    // userPosition is not included into deps because we don't wanna call this useEffect when it changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [value, onFetchPerson, onFetchCompany, onReverseGeocodeAddress]
  );

  const availableDistances = usesSiUnits ? DISTANCES_KM : DISTANCES_MILES;
  const [distance, setDistance] = useState(() => {
    const distanceInMeters = value?.value?.distanceInMeters;
    return usesSiUnits
      ? distanceInMeters
        ? distanceInMeters / 1000
        : DEFAULT_DISTANCE_KM
      : distanceInMeters
        ? distanceInMeters / METERS_IN_MILE
        : DEFAULT_DISTANCE_MILES;
  });

  const handleApplyClick = useCallback(() => {
    const distanceInMeters = distance * (usesSiUnits ? 1000 : METERS_IN_MILE);
    if (isEntityAreaSearchQuery(query)) {
      onChange?.({
        operator: FilterOperator.IN_AREA,
        value: {
          distanceInMeters,
          entity: {
            id: query.entity.id,
            geoPoint: query.entity.geoPoint,
            name: query.entity.name,
            type: query.entity.entity,
          },
        },
      });
    } else if (query?.coordinates) {
      const [longitude, latitude] = query.coordinates;
      onChange?.({
        operator: FilterOperator.IN_AREA,
        value: { distanceInMeters, latitude, location: query.address ?? "", longitude },
      });
    }
  }, [onChange, query, distance, usesSiUnits]);

  const coordinates = useMemo(() => {
    if (!query) {
      return undefined;
    }
    const coordinates = isEntityAreaSearchQuery(query)
      ? query.entity.geoPoint?.coordinates
      : query.coordinates;
    return coordinates ? convertLongLatToLatLngLiteral(coordinates) : undefined;
  }, [query]);

  const handleMapChange = useCallback(
    (updatedCoordinates: google.maps.LatLng, radiusInMeters: number) => {
      if (
        coordinates &&
        (coordinates.lat !== updatedCoordinates.lat() ||
          coordinates.lng !== updatedCoordinates.lng())
      ) {
        // reset address and mode when position is changed
        const coordinates = convertLatLngToLongLat(updatedCoordinates);
        onReverseGeocodeAddress({
          callback: (result) => {
            setQuery({
              address: result.address.formattedAddress ?? "",
              coordinates,
              mode: AreaSearchMode.ADDRESS,
            });
          },
          coordinates,
          failureCallback: () =>
            setQuery({ address: "", coordinates, mode: AreaSearchMode.ADDRESS }),
        });
      }
      setDistance(roundToPrecision(radiusInMeters / (usesSiUnits ? 1000 : METERS_IN_MILE), 2));
    },
    [coordinates, onReverseGeocodeAddress, usesSiUnits]
  );

  return (
    <div className={styles.container}>
      <AreaSearchInput
        availableDistances={availableDistances}
        customDistances={localSettings.getAreaFilterCustomDistances()}
        distance={distance}
        mapCenter={mapCenter}
        maxNumberOfCustomDistances={CUSTOM_DISTANCES_LIMIT}
        onCustomDistancesChange={localSettings.setAreaFilterCustomDistances}
        onDistanceChange={setDistance}
        onQueryChange={setQuery}
        query={query}
        showDynamicLocationsWarning
        userPosition={userPosition}
      />
      <AreaFilterMap
        className={styles.map}
        coordinates={coordinates}
        onChange={handleMapChange}
        radiusInMeters={distance * (localSettings.doesUseSiUnits() ? 1000 : METERS_IN_MILE)}
      />
      <div className={styles.footer}>
        <Button onClick={onHide} size="middle" type="default">
          {intl.formatMessage({
            id: "filters.location.footer.cancel",
            defaultMessage: "Cancel",
            description: "Reset button title in LocationFilter",
          })}
        </Button>
        <Button onClick={handleApplyClick} size="middle" type="primary">
          {intl.formatMessage({
            id: "filters.location.footer.apply",
            defaultMessage: "Apply",
            description: "Apply button title in LocationFilter",
          })}
        </Button>
      </div>
    </div>
  );
};

const mapDispatchToProps = {
  onFetchCompany: fetchCompany,
  onFetchPerson: fetchPerson,
  onReverseGeocodeAddress: reverseGeocodeAddress,
};

export default connect(null, mapDispatchToProps)(LocationFilterPopover);
